// Full medically-responsible patient-education articles.
// Educational only — no dosing or self-treatment instructions.

export const blogPosts = [
  {
    slug: "early-warning-signs-of-diabetes",
    title: "Early Warning Signs of Diabetes",
    category: "Diabetes",
    readTime: "5 min read",
    excerpt:
      "Diabetes often develops silently. Recognising early symptoms — and getting a simple blood test — can prevent years of silent damage.",
    sections: [
      {
        p: [
          "Type 2 diabetes rarely announces itself loudly. In my practice in Bhopal, many patients discover they have diabetes only during a routine checkup or when a blood test is done for some other reason. By then, the sugar may have been elevated for years. The good news is that the body usually gives early signals — if we know what to look for.",
        ],
      },
      {
        h: "Common early symptoms",
        list: [
          "Frequent urination, especially waking up at night to pass urine",
          "Persistent thirst and a dry mouth despite drinking water",
          "Unexplained tiredness or low energy through the day",
          "Increased hunger, even after meals",
          "Blurred vision that comes and goes",
          "Slow-healing cuts, frequent skin or urinary infections",
          "Tingling or numbness in the feet",
        ],
      },
      {
        h: "Who should get tested even without symptoms?",
        p: [
          "You should consider a blood sugar test if you are over 30–35 years of age, have a family history of diabetes, are overweight (especially around the abdomen), have had diabetes during pregnancy, or have conditions like fatty liver, high blood pressure or high cholesterol. In India, diabetes tends to appear earlier and at lower body weights than in Western populations, so waiting for symptoms is not a safe strategy.",
        ],
      },
      {
        h: "Which tests are used?",
        p: [
          "A fasting blood sugar, post-meal blood sugar and HbA1c (which reflects average sugar over about three months) are the standard tests. These are simple, widely available and inexpensive. Your physician interprets them together — a single borderline reading does not always mean diabetes, and a normal reading does not always rule it out.",
        ],
      },
      {
        h: "What to do if your sugar is borderline",
        p: [
          "A 'pre-diabetes' result is an opportunity, not a verdict. Structured changes in diet, regular physical activity, weight management and good sleep can significantly delay or even prevent progression. This is best done under medical guidance rather than through crash diets or unverified remedies.",
        ],
      },
      {
        p: [
          "If you recognise any of these symptoms — or fall in a higher-risk group — a consultation and a simple blood test are a sensible next step. Early evaluation makes management far easier.",
        ],
      },
    ],
  },
  {
    slug: "high-blood-pressure-when-to-consult-a-doctor",
    title: "High Blood Pressure: When Should You Consult a Doctor?",
    category: "Heart & BP",
    readTime: "5 min read",
    excerpt:
      "Hypertension is called the silent killer because it usually causes no symptoms while damaging the heart, brain and kidneys. Here is when to act.",
    sections: [
      {
        p: [
          "High blood pressure (hypertension) is one of the most common conditions I manage, and one of the most under-appreciated. Most people with high BP feel completely fine. Meanwhile, uncontrolled pressure quietly strains the heart, brain, kidneys and eyes over years — until it presents as a stroke, heart attack or kidney failure. This is why hypertension is called the 'silent killer'.",
        ],
      },
      {
        h: "Understanding your numbers",
        p: [
          "A reading below 120/80 mmHg is generally considered normal. Readings between 120–139 systolic or 80–89 diastolic are a warning zone. Consistent readings of 140/90 mmHg or higher usually meet the definition of hypertension, though your physician will confirm this with repeated or home measurements before labeling it.",
        ],
      },
      {
        h: "When should you consult a doctor?",
        list: [
          "Your readings are repeatedly at or above 140/90 mmHg on different days",
          "You are over 30 and have never had your BP checked",
          "You have diabetes, kidney disease, or a family history of early heart disease",
          "You are on BP medicines but readings remain high or you feel side effects",
          "You get headaches, dizziness, chest discomfort or breathlessness along with high readings",
        ],
      },
      {
        h: "When is it an emergency?",
        p: [
          "A very high reading (for example 180/120 mmHg or more) together with severe headache, chest pain, blurred vision, confusion, weakness of a limb or difficulty speaking needs immediate emergency care — do not wait for a routine appointment.",
        ],
      },
      {
        h: "What treatment usually involves",
        p: [
          "Management is not only about tablets. Salt reduction, weight management, regular walking, limiting alcohol, good sleep and stress control all genuinely lower blood pressure. When medicines are needed, they are chosen based on your age, other conditions and reports — and reviewed periodically. Never stop BP medicines on your own because you 'feel fine'; the medicine is precisely why you feel fine.",
        ],
      },
      {
        p: [
          "If you have never checked your blood pressure, or have been postponing a review, a consultation and baseline evaluation is a simple, worthwhile step.",
        ],
      },
    ],
  },
  {
    slug: "persistent-fever-causes-and-when-to-seek-medical-advice",
    title: "Persistent Fever: Common Causes and When to Seek Medical Advice",
    category: "Infections",
    readTime: "5 min read",
    excerpt:
      "Most fevers settle in a few days. A fever that persists, keeps returning, or comes with warning signs needs proper medical evaluation.",
    sections: [
      {
        p: [
          "Fever is the body's natural response to infection, and most fevers — especially viral ones — settle within three to five days with rest, fluids and simple supportive care. But in Bhopal, we also see fevers due to dengue, malaria, typhoid and other infections that need specific testing and treatment. Knowing when a fever needs a doctor makes all the difference.",
        ],
      },
      {
        h: "Common causes of fever in adults",
        list: [
          "Viral infections (including seasonal flu and viral fevers)",
          "Dengue, malaria and chikungunya — especially in and after monsoon",
          "Typhoid (enteric fever) — typically a prolonged, sustained fever",
          "Urinary tract infections",
          "Respiratory infections, including pneumonia",
          "Less commonly, tuberculosis and non-infective causes",
        ],
      },
      {
        h: "Sensible home care while you monitor",
        p: [
          "Rest, adequate fluids, light food and temperature recording two to three times a day are reasonable. Avoid self-medicating with antibiotics — they do not work against viral fevers and can mask or complicate other illnesses. Avoid combining multiple painkiller brands, as many contain the same ingredient.",
        ],
      },
      {
        h: "When to seek medical advice",
        list: [
          "Fever beyond 3 days, or fever that keeps returning",
          "Temperature above 103°F (39.4°C) that does not settle",
          "Fever with severe headache, vomiting, rash, bleeding gums or reduced urine",
          "Breathlessness, chest pain, confusion, extreme weakness or stiff neck",
          "Fever in someone with diabetes, kidney disease, low immunity, pregnancy, or in the elderly",
        ],
      },
      {
        h: "What the doctor will typically do",
        p: [
          "A physician will examine you, ask about travel, food and water exposure, and order targeted tests — such as a blood count, malaria/dengue tests, typhoid test or urine examination — based on the pattern. This avoids both under-testing and unnecessary testing, and ensures the right treatment rather than guesswork.",
        ],
      },
      {
        p: [
          "If your fever has persisted beyond a few days or comes with any warning sign, do not wait it out — get evaluated.",
        ],
      },
    ],
  },
  {
    slug: "thyroid-disorders-common-symptoms",
    title: "Thyroid Disorders: Common Symptoms",
    category: "Hormones",
    readTime: "5 min read",
    excerpt:
      "Thyroid problems are extremely common, especially in women, and their symptoms are often mistaken for 'just stress' or ageing.",
    sections: [
      {
        p: [
          "The thyroid is a small gland in the neck that controls the body's metabolic speed. When it produces too little hormone (hypothyroidism) everything slows down; when it produces too much (hyperthyroidism) everything speeds up. Thyroid disorders are very common in India — particularly among women — and are easily diagnosed with a blood test.",
        ],
      },
      {
        h: "Symptoms of an underactive thyroid (hypothyroidism)",
        list: [
          "Persistent tiredness and low energy",
          "Weight gain despite no change in diet",
          "Feeling unusually cold",
          "Constipation, dry skin and hair fall",
          "Slowed thinking, low mood or forgetfulness",
          "Irregular or heavy periods",
        ],
      },
      {
        h: "Symptoms of an overactive thyroid (hyperthyroidism)",
        list: [
          "Unintentional weight loss despite good appetite",
          "Palpitations — a racing or pounding heartbeat",
          "Trembling hands, anxiety or irritability",
          "Excessive sweating and heat intolerance",
          "Frequent loose motions",
          "Disturbed sleep",
        ],
      },
      {
        h: "How thyroid problems are diagnosed",
        p: [
          "A simple blood test — TSH, often with T3 and T4 — answers most questions. One important caution: mild abnormalities, especially a slightly raised TSH, do not always need treatment. Results must be interpreted along with your symptoms, age and overall health. Starting lifelong thyroid medicine on the basis of a single borderline report, without proper evaluation, is a common mistake.",
        ],
      },
      {
        h: "Living well with a thyroid condition",
        p: [
          "If medication is needed, it is safe, inexpensive and effective — but it works best when taken consistently, usually on an empty stomach, and reviewed with periodic blood tests. The dose often needs adjustment over time, so regular follow-up with your physician matters more than any single report.",
        ],
      },
      {
        p: [
          "If several of these symptoms sound familiar, a thyroid profile and a consultation will give you a clear answer.",
        ],
      },
    ],
  },
  {
    slug: "understanding-cholesterol-and-heart-health",
    title: "Understanding Cholesterol and Heart Health",
    category: "Heart & BP",
    readTime: "5 min read",
    excerpt:
      "Cholesterol reports confuse most people. Here is what LDL, HDL and triglycerides actually mean — and what genuinely protects your heart.",
    sections: [
      {
        p: [
          "Cholesterol is not a poison — it is essential for building cells and hormones. The problem arises when certain cholesterol particles circulate in excess for years, depositing in artery walls and narrowing them. This process, called atherosclerosis, begins silently in our 20s and 30s and is the root cause of most heart attacks and strokes.",
        ],
      },
      {
        h: "Reading your lipid report",
        list: [
          "LDL ('bad' cholesterol): the main driver of artery blockage — lower is better",
          "HDL ('good' cholesterol): protective — higher is better",
          "Triglycerides: blood fats that rise with sugar intake, alcohol and excess weight",
          "Total cholesterol: a summary number — less important on its own than the parts",
        ],
      },
      {
        h: "Why one number is not the whole story",
        p: [
          "Whether your cholesterol needs treatment depends on your overall cardiovascular risk — your age, blood pressure, sugar status, smoking, and family history of early heart disease. The same LDL value may be acceptable in a healthy 25-year-old but need active treatment in someone with diabetes. This is why lipid reports are best interpreted by a physician, not in isolation.",
        ],
      },
      {
        h: "What genuinely helps",
        list: [
          "Replacing fried and processed food with whole grains, pulses, vegetables and nuts",
          "At least 30 minutes of brisk walking or equivalent activity most days",
          "Maintaining a healthy waist size and body weight",
          "Avoiding tobacco completely and limiting alcohol",
          "Managing blood sugar and blood pressure alongside",
        ],
      },
      {
        h: "Do I need medicines?",
        p: [
          "For many people with moderately high cholesterol and low overall risk, structured lifestyle change is the first step, with a repeat test after a few months. When cholesterol is very high, or overall risk is significant (for example in diabetes or after a heart event), medicines such as statins are recommended — they are among the most well-studied and effective preventive treatments in modern medicine.",
        ],
      },
      {
        p: [
          "If your last lipid profile is more than a year or two old — or you have never had one — consider getting tested and reviewed.",
        ],
      },
    ],
  },
  {
    slug: "fatty-liver-risk-factors-and-prevention",
    title: "Fatty Liver: Risk Factors and Prevention",
    category: "Liver Health",
    readTime: "5 min read",
    excerpt:
      "Fatty liver is now one of the most common findings on ultrasound reports — and in most cases it is reversible with the right approach.",
    sections: [
      {
        p: [
          "'Grade 1 fatty liver' is one of the most common lines I see on ultrasound reports in Bhopal. Fatty liver disease means excess fat has accumulated in liver cells. In its early stages it causes no symptoms and no liver damage — but left unaddressed for years, in some people it can progress to inflammation and scarring of the liver. The encouraging part: early fatty liver is largely reversible.",
        ],
      },
      {
        h: "Common risk factors",
        list: [
          "Excess weight, especially around the abdomen",
          "Diabetes or pre-diabetes",
          "High triglycerides and cholesterol",
          "Sedentary lifestyle and frequent intake of sugary or fried food",
          "Regular alcohol consumption",
        ],
      },
      {
        h: "Does fatty liver cause symptoms?",
        p: [
          "Usually not. Most people discover it incidentally on an ultrasound done for some other reason. Some notice vague fullness or discomfort on the right side of the abdomen, or unexplained fatigue, but these are non-specific. This is exactly why metabolic health — weight, sugar, lipids — should be evaluated even when you feel well.",
        ],
      },
      {
        h: "How it is evaluated",
        p: [
          "An ultrasound is the usual starting point. Your physician will generally add liver function tests, blood sugar or HbA1c, and a lipid profile to understand the full metabolic picture. In selected cases, further tests assess whether any significant liver scarring has developed.",
        ],
      },
      {
        h: "What actually reverses it",
        list: [
          "Gradual weight loss — even 5–10% of body weight measurably reduces liver fat",
          "Cutting back on sugar, refined carbohydrates and sugary drinks",
          "Regular physical activity, including walking",
          "Avoiding alcohol",
          "Treating associated diabetes, cholesterol and blood pressure",
        ],
      },
      {
        p: [
          "Be cautious of products marketed as 'liver cleansers' or quick detox cures — there is no shortcut. If fatty liver appears on your report, a proper metabolic evaluation is the right response.",
        ],
      },
    ],
  },
  {
    slug: "common-causes-of-weakness-and-fatigue",
    title: "Common Causes of Weakness and Fatigue",
    category: "General Health",
    readTime: "5 min read",
    excerpt:
      "Constant tiredness is one of the most common complaints in OPD — and it usually has a findable, treatable cause.",
    sections: [
      {
        p: [
          "'Doctor, I just feel tired all the time' is among the most frequent concerns I hear. Fatigue is a symptom, not a diagnosis — and it deserves a proper evaluation rather than random tonics or multivitamins. In most patients, a careful history, examination and a few targeted tests identify a treatable cause.",
        ],
      },
      {
        h: "Common medical causes we look for",
        list: [
          "Anemia (low haemoglobin) — very common, especially in women",
          "Vitamin B12 and Vitamin D deficiency",
          "Underactive thyroid",
          "Uncontrolled or undiagnosed diabetes",
          "Recovery phase after viral infections",
          "Kidney or liver conditions",
          "Poor sleep quality, including snoring-related sleep problems",
        ],
      },
      {
        h: "Lifestyle causes are equally real",
        p: [
          "Erratic sleep, prolonged screen time, minimal physical activity, skipped meals, excessive tea or coffee, and chronic stress are frequent contributors. Mental health matters too — persistent low mood and anxiety often present as physical exhaustion. A good evaluation considers all of these, not just the lab report.",
        ],
      },
      {
        h: "Why self-prescribing supplements is risky",
        p: [
          "Taking iron, B12 or calcium 'just in case' can mask the real problem, delay the correct diagnosis, and occasionally cause harm — iron overload and excessive vitamin D are genuine risks. The sensible sequence is: test first, treat what is actually deficient, then recheck.",
        ],
      },
      {
        h: "When fatigue needs prompt attention",
        p: [
          "Seek medical advice soon if tiredness comes with unexplained weight loss, persistent fever, breathlessness on mild exertion, chest discomfort, black stools, or if it is progressively worsening rather than fluctuating.",
        ],
      },
      {
        p: [
          "If fatigue has persisted for more than a few weeks, do not just push through it — a structured evaluation usually finds the answer.",
        ],
      },
    ],
  },
  {
    slug: "why-regular-health-checkups-matter",
    title: "Why Regular Health Checkups Matter",
    category: "Prevention",
    readTime: "5 min read",
    excerpt:
      "Most chronic diseases give no symptoms for years. Periodic screening finds them early — when they are easiest and least expensive to manage.",
    sections: [
      {
        p: [
          "Diabetes, hypertension, high cholesterol and early kidney disease have one thing in common: they cause no symptoms in their early, most treatable stages. People feel perfectly healthy while silent changes accumulate. Regular health checkups exist precisely for this gap — to find problems before they announce themselves.",
        ],
      },
      {
        h: "What a sensible adult checkup usually covers",
        list: [
          "Blood pressure measurement",
          "Blood sugar (fasting and/or HbA1c)",
          "Lipid profile (cholesterol and triglycerides)",
          "Kidney and liver function tests",
          "Complete blood count (screens for anemia and infections)",
          "Thyroid function, particularly for women and those with symptoms",
          "Urine examination",
        ],
      },
      {
        h: "How often should you get tested?",
        p: [
          "For most healthy adults under 30 with no risk factors, a baseline checkup every two to three years is reasonable. After 30–35 — or earlier if you have a family history of diabetes, heart disease or hypertension, are overweight, or smoke — an annual review is a sensible investment. Those already living with conditions like diabetes or hypertension need more frequent, targeted monitoring.",
        ],
      },
      {
        h: "Checkups are not one-size-fits-all",
        p: [
          "Large 'full body packages' with dozens of tests are often marketed aggressively, but more tests do not mean better health — they can produce confusing false alarms. A physician-guided checkup selects tests based on your age, symptoms, family history and risk profile, and — most importantly — someone interprets the results and tells you what actually needs action.",
        ],
      },
      {
        h: "The real value: trend over time",
        p: [
          "A single report is a snapshot; a series of reports is a story. When your physician can compare this year's sugar, pressure and cholesterol with previous years, early drifts are caught long before they become diseases. That continuity is the true benefit of periodic checkups with the same physician.",
        ],
      },
      {
        p: [
          "If you cannot remember your last health evaluation, that is your answer — schedule one.",
        ],
      },
    ],
  },
];

export const getPost = (slug) => blogPosts.find((p) => p.slug === slug);
